/**
 * useClientMarkups Hook (Story 17.2 — Epic 17 Ad Spend Markup)
 *
 * Reads and writes a client's per-client / per-ad-platform markup config via
 * GET/PUT /api/clients/:id/markups (the replace-set endpoint). Follows the
 * useClientIntegrations.ts shape (TanStack Query, disabled without clientId).
 *
 * - `useClientMarkups(clientId)` → the config rows, key ["client-markups", id].
 * - `useSaveClientMarkups(clientId)` → mutation that PUTs the full set and
 *   invalidates ["client-markups", id] on success.
 *
 * Markup is keyed by integration TYPE, never by integration row (Trap #1):
 * `integrationType: null` is the GLOBAL default; a string is a platform slug.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPut } from "../lib/apiClient";

// ============================================================================
// Types (mirror the server response contract in routes/clients/markups.ts)
// ============================================================================

export type MarkupType = "percentage" | "fixed";

/** One stored markup row. `integrationType: null` = the global default. */
export interface ClientMarkupRow {
  id: string;
  integrationType: string | null;
  markupType: MarkupType;
  markupValue: number;
  /** Epoch-ms. */
  updatedAt: number;
}

/** A type+value pair as accepted by the PUT payload. */
export interface MarkupConfigInput {
  markupType: MarkupType;
  markupValue: number;
}

/** A per-platform override in the PUT payload. */
export interface MarkupOverrideInput extends MarkupConfigInput {
  integrationType: string;
}

/** The declarative full-set replace payload (AC #2). */
export interface SaveClientMarkupsPayload {
  /** null → no global default (the row is removed on replace). */
  global: MarkupConfigInput | null;
  overrides: MarkupOverrideInput[];
}

// ============================================================================
// Read hook
// ============================================================================

export function useClientMarkups(clientId: string | undefined) {
  const {
    data: markups = [],
    isLoading,
    isError,
  } = useQuery<ClientMarkupRow[]>({
    queryKey: ["client-markups", clientId],
    queryFn: async () => {
      const res = await apiGet(`/api/clients/${clientId}/markups`);
      if (!res.ok) return [];
      const json = (await res.json()) as { data: { markups: ClientMarkupRow[] } };
      return json.data.markups;
    },
    enabled: !!clientId,
  });

  return { markups, isLoading, isError };
}

// ============================================================================
// Save mutation
// ============================================================================

export function useSaveClientMarkups(clientId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      payload: SaveClientMarkupsPayload
    ): Promise<ClientMarkupRow[]> => {
      const res = await apiPut(`/api/clients/${clientId}/markups`, payload);
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { detail?: string; title?: string }
          | null;
        throw new Error(
          body?.detail ?? body?.title ?? `Failed to save markup settings (${res.status})`
        );
      }
      const json = (await res.json()) as { data: { markups: ClientMarkupRow[] } };
      return json.data.markups;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["client-markups", clientId],
      });
    },
  });
}
